import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Button,
  Collapse,
  Grid,
  Alert,
  Link,
} from '@mui/material';
import {
  FactCheck as FactCheckIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { format } from 'date-fns';
import { api, MatchReviewItem } from '../services/api-client';

const STATUS_OPTIONS = ['pending', 'reviewed', 'approved', 'rejected', 'all'];
const PAGE_SIZE = 50;

type ChipColor = 'warning' | 'info' | 'success' | 'error' | 'default';

const statusColor = (status: string): ChipColor => {
  switch (status) {
    case 'pending':
      return 'warning';
    case 'reviewed':
      return 'info';
    case 'approved':
      return 'success';
    case 'rejected':
      return 'error';
    default:
      return 'default';
  }
};

const formatTimestamp = (ts: string | null): string => {
  if (!ts) return 'Unknown';
  const date = new Date(ts);
  return isNaN(date.getTime()) ? String(ts) : format(date, 'MMM d, yyyy h:mm a');
};

// Google Maps deep links so a reviewer can eyeball the place, not just a ULID.
const mapsPlaceUrl = (placeId: string) =>
  // Maps URLs API form — the legacy maps/place/?q=place_id: form returns
  // "No results found on Google Maps" (same fix as concierge-ui, 2026-07-08).
  `https://www.google.com/maps/search/?api=1&query=place&query_place_id=${encodeURIComponent(placeId)}`;
const mapsCoordUrl = (lat: number, lng: number) =>
  `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
// The candidate place_id the verifier/judge couldn't confirm — lives in signals.
const candidateOf = (item: MatchReviewItem): string | null => {
  const c = item.signals?.candidate_place_id;
  return typeof c === 'string' && c && c !== 'None' ? c : null;
};

// Human-readable evidence chips from the raw verify signals (raw JSON stays in the
// expand). Only render what exists — most abstains only have a verify_reason.
const signalChips = (s: Record<string, any> | null | undefined): string[] => {
  if (!s) return [];
  const chips: string[] = [];
  const d = s.distance_km;
  if (typeof d === 'number') chips.push(d < 1 ? `${Math.round(d * 1000)} m apart` : `${d.toFixed(1)} km apart`);
  if (s.phone_match === true) chips.push('phone ✓');
  if (s.phone_match === false) chips.push('phone ✗');
  if (typeof s.name_sim === 'number') chips.push(`name ${(s.name_sim * 100).toFixed(0)}%`);
  if (s.verify_reason && typeof s.verify_reason === 'string') chips.push(s.verify_reason);
  return chips;
};

export default function MatchReviewQueue() {
  const [statusFilter, setStatusFilter] = useState('pending');
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState<{ [key: string]: boolean }>({});

  const offset = page * PAGE_SIZE;

  const { data, isLoading, error } = useQuery(
    ['matchReviewQueue', statusFilter, entityTypeFilter, page],
    () =>
      api.getMatchReviewQueue({
        status: statusFilter,
        entityType: entityTypeFilter || undefined,
        limit: PAGE_SIZE,
        offset,
      }),
    { refetchInterval: 30000, keepPreviousData: true }
  );

  const queryClient = useQueryClient();
  const updateItem = useMutation(
    (vars: { entity_type: string; entity_id: string; reason: string; status: string }) =>
      api.updateMatchReviewItem(vars),
    { onSuccess: () => queryClient.invalidateQueries('matchReviewQueue') }
  );
  // Surface the backend's reason (e.g. the approve collision-guard 409) instead of a
  // generic failure — the reviewer needs to know WHY nothing changed.
  const updateErrorMsg =
    updateItem.isError && updateItem.error instanceof Error
      ? updateItem.error.message
      : null;

  const rowKey = (item: MatchReviewItem) =>
    item.id || `${item.entity_type}:${item.entity_id}:${item.reason}`;

  const toggle = (key: string) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const onStatusChange = (value: string) => {
    setStatusFilter(value);
    setPage(0);
  };
  const onEntityTypeChange = (value: string) => {
    setEntityTypeFilter(value);
    setPage(0);
  };

  if (isLoading) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Match Review Queue
        </Typography>
        <Typography>Loading review queue...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Match Review Queue
        </Typography>
        <Alert severity="error">Failed to load the match review queue</Alert>
      </Box>
    );
  }

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <FactCheckIcon color="primary" sx={{ fontSize: 40 }} />
        <Box>
          <Typography variant="h4">Match Review Queue</Typography>
          <Typography variant="body2" color="text.secondary">
            Place ID verify escalates &amp; rejects awaiting human review
          </Typography>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => onStatusChange(e.target.value)}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Entity Type"
                variant="outlined"
                size="small"
                placeholder="e.g. location"
                value={entityTypeFilter}
                onChange={(e) => onEntityTypeChange(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                {total} row{total !== 1 ? 's' : ''} match this filter
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {updateItem.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {updateErrorMsg || 'Failed to update the item. Please retry.'}
        </Alert>
      )}

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Created</TableCell>
              <TableCell>Place</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Candidate place</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => {
              const key = rowKey(item);
              return (
                <React.Fragment key={key}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton size="small" onClick={() => toggle(key)}>
                        {expanded[key] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatTimestamp(item.created_at)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {item.entity_name || '(name unavailable)'}
                      </Typography>
                      {item.entity_address && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {item.entity_address}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {[item.entity_city, item.entity_country, item.entity_type]
                          .filter(Boolean)
                          .join(' · ')}
                        {item.entity_lat != null && item.entity_lng != null && (
                          <>
                            {' · '}
                            <Link
                              href={mapsCoordUrl(item.entity_lat, item.entity_lng)}
                              target="_blank"
                              rel="noopener"
                            >
                              its location ↗
                            </Link>
                          </>
                        )}
                        {item.entity_place_id && (
                          <>
                            {' · '}
                            <Link
                              href={mapsPlaceUrl(item.entity_place_id)}
                              target="_blank"
                              rel="noopener"
                            >
                              current place ↗
                            </Link>
                          </>
                        )}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{ display: 'block', fontFamily: 'monospace' }}
                      >
                        {item.entity_id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={item.reason} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      {typeof item.signals?.candidate_name === 'string' && (
                        <Typography variant="body2" sx={{ display: 'block' }}>
                          {item.signals.candidate_name}
                        </Typography>
                      )}
                      {typeof item.signals?.candidate_address === 'string' && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {item.signals.candidate_address}
                        </Typography>
                      )}
                      {candidateOf(item) ? (
                        <Link
                          href={mapsPlaceUrl(candidateOf(item)!)}
                          target="_blank"
                          rel="noopener"
                          sx={{ fontSize: 13 }}
                        >
                          View on Maps ↗
                        </Link>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          none
                        </Typography>
                      )}
                      <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {signalChips(item.signals).map((c) => (
                          <Chip key={c} label={c} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={item.status} size="small" color={statusColor(item.status)} />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <Button
                          size="small"
                          color="success"
                          disabled={updateItem.isLoading}
                          onClick={() =>
                            updateItem.mutate({
                              entity_type: item.entity_type,
                              entity_id: item.entity_id,
                              reason: item.reason,
                              status: 'approved',
                            })
                          }
                        >
                          Approve
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          disabled={updateItem.isLoading}
                          onClick={() =>
                            updateItem.mutate({
                              entity_type: item.entity_type,
                              entity_id: item.entity_id,
                              reason: item.reason,
                              status: 'rejected',
                            })
                          }
                        >
                          Reject
                        </Button>
                        {/* bookkeeping-only: for rows with nothing to install/cache
                            (the approve 409 explicitly points here) */}
                        <Button
                          size="small"
                          color="inherit"
                          disabled={updateItem.isLoading}
                          onClick={() =>
                            updateItem.mutate({
                              entity_type: item.entity_type,
                              entity_id: item.entity_id,
                              reason: item.reason,
                              status: 'reviewed',
                            })
                          }
                        >
                          Reviewed
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 0 }}>
                      <Collapse in={expanded[key]} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Signals
                          </Typography>
                          <Paper variant="outlined" sx={{ p: 2 }}>
                            <Box
                              component="pre"
                              sx={{
                                m: 0,
                                fontSize: 12,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                              }}
                            >
                              {JSON.stringify(item.signals ?? {}, null, 2)}
                            </Box>
                          </Paper>
                          {item.notes && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              <strong>Notes:</strong> {item.notes}
                            </Typography>
                          )}
                          {item.reviewed_by && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              <strong>Reviewed by:</strong> {item.reviewed_by}
                              {item.reviewed_at ? ` on ${formatTimestamp(item.reviewed_at)}` : ''}
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {items.length === 0 && (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No rows match this filter</Typography>
        </Box>
      )}

      {/* Pagination */}
      <Box
        sx={{
          mt: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Page {page + 1} · showing {items.length} of {total}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          disabled={page === 0}
          onClick={() => setPage((p) => Math.max(0, p - 1))}
        >
          Previous
        </Button>
        <Button
          size="small"
          variant="outlined"
          disabled={offset + items.length >= total}
          onClick={() => setPage((p) => p + 1)}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
}
